import { AxiosError } from 'axios'
import { DataTableController } from 'components/common'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import DeleteStoreImportButton from 'components/common/dataTable/deleteStoreImportButton'
import ActiveFilterChip from 'components/common/activeFilterChip'
import {
  MasterDataBarcodeSearch,
  MasterDataFilterForm,
  MasterDataTable,
  columns,
} from 'components/masterData'
import { FILTER_FIELD_MASTER_DATA } from 'components/masterData/masterDataFilterForm'
import {
  BARCODE_ERROR_MESSAGES,
  BARCODE_COUNT_KEY,
  BARCODE_QUERY_KEY,
  BARCODE_SEARCH_EXPIRED,
  BARCODE_TOKEN_KEY,
  getBarcodeErrorCode,
} from 'components/masterData/masterDataBarcodeSearch'
import { ImportColumn } from 'components/common/importModal'
import { usePageAccess, useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import toast from 'react-hot-toast'
import {
  dowloadMasterData,
  importMasterData,
  importCatalogueCategories,
} from 'utils/api/masterDataApi'
import {
  importDeleteStoreAssociation,
  importPharmacyCatalogue,
} from 'utils/api/pharmacyApi'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import { fetchConfigurations } from 'utils/api/configApi'
import { filterEmptyValues } from 'utils/helpers'

const MasterData = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])
  const [enable3PLProductImport, setEnable3PLProductImport] =
    useState<boolean>(false)
  const [categoryImportColumns, setCategoryImportColumns] = useState<
    ImportColumn[]
  >([])
  const [pharmacyImportColumns, setPharmacyImportColumns] = useState<
    ImportColumn[]
  >([])

  const { getQuery, setMultipleQueries } = useQuery()

  useEffect(() => {
    getImportTemplate('masterData')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetchConfigurations({ fields: ['pharmacy'] })
      .then(({ data }) =>
        setEnable3PLProductImport(!!data?.pharmacy?.enable3PLProductImport),
      )
      .catch(() => {})
  }, [])

  useEffect(() => {
    getImportTemplate('catalogueCategories')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setCategoryImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  useEffect(() => {
    getImportTemplate('pharmacyCatalogue')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setPharmacyImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_MASTER_DATA)
    const barcodeToken = getQuery(BARCODE_TOKEN_KEY)
    const barcode = getQuery(BARCODE_QUERY_KEY)

    // filtering empty values and keys of filter
    const masterFilter = filters ? filterEmptyValues(filters) : {}

    // A barcode search on its own is enough to scope the export; otherwise at
    // least one filter is required.
    if (!barcodeToken && !barcode && Object.keys(masterFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await dowloadMasterData({ filters: masterFilter, barcodeToken, barcode })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      // Token expired mid-flight → clear the search and prompt a re-upload.
      if (getBarcodeErrorCode(error) === BARCODE_SEARCH_EXPIRED) {
        setMultipleQueries({
          [BARCODE_TOKEN_KEY]: undefined,
          [BARCODE_COUNT_KEY]: undefined,
          page: 1,
        })
        return toast.error(BARCODE_ERROR_MESSAGES[BARCODE_SEARCH_EXPIRED], {
          id: loadingToast,
          duration: 4000,
        })
      }
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export Master Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('masterData')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'master_data_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadCategorySample = async () => {
    const response = await downloadImportSample('catalogueCategories')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'catalogue_categories_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadPharmacySample = async () => {
    const response = await downloadImportSample('pharmacyCatalogue')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = '3pl_catalogue_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Master Data</title>
      </Helmet>
      <div className="flex justify-end">
        {isEditor && importPharmacyCatalogue && enable3PLProductImport && (
          <div className="flex gap-2">
            <DataTableImportButton
              label="Import 3PL Catalogue"
              disabled={loading}
              uploadApiFunction={importPharmacyCatalogue}
              className="mb-5 border border-gray-300"
              importColumns={pharmacyImportColumns}
              importModuleName="3PL catalogue"
              onDownloadSample={handleDownloadPharmacySample}
            />
            <DeleteStoreImportButton
              label="Delete Store & Chain Association"
              disabled={loading}
              uploadApiFunction={importDeleteStoreAssociation}
              className="mb-5 border border-gray-300"
            />
          </div>
        )}
      </div>
      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleExport={handleExport}
        handleImport={importMasterData}
        activeColumnKey="master"
        placeholder="Search by barcode and description"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
        importColumns={importColumns}
        importModuleName="master data"
        onDownloadSample={handleDownloadSample}
      />

      {isEditor && (
        <div className="mb-3 flex items-center gap-3">
          <DataTableImportButton
            label="Import Categories"
            disabled={loading}
            uploadApiFunction={importCatalogueCategories}
            className="border border-gray-300"
            importColumns={categoryImportColumns}
            importModuleName="catalogue categories"
            onDownloadSample={handleDownloadCategorySample}
          />
        </div>
      )}

      <MasterDataBarcodeSearch />

      {!showFilter && <ActiveFilterChip filterKey={FILTER_FIELD_MASTER_DATA} />}

      {showFilter ? (
        <MasterDataFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <MasterDataTable
          setSelectedColumns={setSelectedColumns}
          isEditor={isEditor}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default MasterData
